import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  ActionIconContainer,
  AddFormButton,
  CustomLoader,
  FilterButton,
} from "../../../../shared/components/TableElements";
import {
  FormGroupLabel,
  FormItem,
} from "../../../../shared/components/form/FormElements";
import { formInputStyles } from "../../../../shared/constants/styles";
import {
  handleNegative,
  handleNumberInputOnWheelPreventChange,
} from "../../../../shared/helpers";
import FormField from "../../../../shared/components/form/FormField";
import { useForm } from "react-hook-form";
import Error from "@/shared/components/form/Error";
//import { colorLightGray } from "@/utils/palette";
import { post, postApi } from "../../../../api/getUser";

const TopUpCreditsModal = ({
  show,
  onHide,
  //onPurchase,
  //saveTopUpLoading,
  // Optional props for price details (passed from parent)
  pricePerCredit: propPricePerCredit,
  currency: propCurrency,
  fromScreen,
}) => {
  const { t } = useTranslation("common");
  const [selectedAmount, setSelectedAmount] = useState(null);
  //const [customAmount, setCustomAmount] = useState(null);
  //const [isCustomSelected, setIsCustomSelected] = useState(false);

  //const [credits, setCredits] = useState(0);
  const {
    control,
    //getValues,
    //watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({ mode: "onBlur" });

  const [totalCredits, setTotalCredits] = useState(0);
  const [pricePerCredit, setPricePerCredit] = useState(
    propPricePerCredit || 0.25
  );
  const [currency, setCurrency] = useState(propCurrency || "USD");
  const [priceLoading, setPriceLoading] = useState(
    !propPricePerCredit || !propCurrency
  );

  const predefinedOptions = useMemo(
    () => [
      { id: 1, credits: 30, price: 30 * pricePerCredit },
      { id: 2, credits: 50, price: 50 * pricePerCredit },
      { id: 3, credits: 100, price: 100 * pricePerCredit },
    ],
    [pricePerCredit]
  );

  useEffect(() => {
    // Only fetch price details if not provided as props
    if (!propPricePerCredit || !propCurrency) {
      setPriceLoading(true);
      (async () => {
        try {
          const priceDetailsRes = await postApi(
            {
              querystring: "get_price_deatils",
            },
            "subscription"
          );
          if (priceDetailsRes.success) {
            const {
              topup: { price, currency },
            } = priceDetailsRes;
            setPricePerCredit(price);
            setCurrency(currency);
          }
        } catch (error) {
          console.error("Error fetching price details:", error);
        } finally {
          setPriceLoading(false);
        }
      })();
    } else {
      // Use props if provided
      setPricePerCredit(propPricePerCredit);
      setCurrency(propCurrency);
      setPriceLoading(false);
    }
  }, [propPricePerCredit, propCurrency]);

  const handlePredefinedSelect = (option) => {
    setSelectedAmount(option);
    //setIsCustomSelected(false);
    setTotalCredits(option.credits);
    setValue("customCredits", "");
    clearErrors("customCredits");
  };

  //   const handleCustomAmountChange = (value) => {
  //     setTotalCredits(parseInt(value) || 0);
  //     setCustomAmount(parseInt(value) || null);
  //     setIsCustomSelected(true);
  //     setSelectedAmount(null);
  //   };

  //   const getTotalPrice = () => {
  //     if (isCustomSelected) {
  //       return (customAmount * pricePerCredit).toFixed(2) || "0.00";
  //     }
  //     return selectedAmount ? selectedAmount.price.toFixed(2) : "0.00";
  //   };

  const getTotalPrice = () =>
    (+totalCredits * +pricePerCredit)?.toFixed(2) || "0.00";

  const handlePurchase = async () => {
    const res = await post("subscription", {
      querystring: "create_topup_checkout_session",
      quantity: totalCredits,
      return_screen: fromScreen,
    });
    console.log("res", res);
    if (res.statusCode === 200) {
      window.location.href = res.url;
    }
    //onPurchase({ credits: totalCredits, price: getTotalPrice() });
    //onHide();
  };

  const handleClose = () => {
    setSelectedAmount(null);
    setTotalCredits(0);
    setValue("customCredits", "");
    clearErrors("customCredits");
    //setIsCustomSelected(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="md">
      <Modal.Header>
        <h4>{t("top_up_credits")}</h4>
        <div className="d-flex justify-content-end">
          <ActionIconContainer onClick={handleClose}>
            <i className="fi fi-rr-cross-small" />
          </ActionIconContainer>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-4">
          <FormGroupLabel className="m-0">{t("choose_amount")}</FormGroupLabel>
          <div className="d-flex gap-3 mt-2 align-items-center">
            {predefinedOptions.map((option, index) => (
              <PredefinedOptionCard
                key={index}
                selected={selectedAmount?.id === option.id}
                onClick={() => !priceLoading && handlePredefinedSelect(option)}
                style={{
                  opacity: priceLoading ? 0.6 : 1,
                  cursor: priceLoading ? "not-allowed" : "pointer",
                }}
              >
                <p>
                  {option.credits} {t("credits")}
                </p>
                {priceLoading ? (
                  <div className="d-flex justify-content-center">
                    <CustomLoader />
                  </div>
                ) : (
                  <p>{`${currency} ${option.price.toFixed(2)}`}</p>
                )}
              </PredefinedOptionCard>
            ))}
          </div>
        </div>

        <div className="mb-2">
          <FormGroupLabel className="m-0">
            {t("or_enter_custom_amount")}
          </FormGroupLabel>
          <div className="d-flex align-items-center gap-2 mt-1">
            <FormItem style={{ minWidth: "220px" }}>
              <FormField
                name="customCredits"
                control={control}
                component="input"
                type="number"
                onKeyDown={handleNegative}
                onWheel={handleNumberInputOnWheelPreventChange}
                step="5"
                min="5"
                errors={errors}
                defaultValue=""
                placeholder={t("enter_credits_placeholder")}
                style={{
                  ...formInputStyles,
                  opacity: priceLoading ? 0.6 : 1,
                  cursor: priceLoading ? "not-allowed" : "text",
                }}
                disabled={priceLoading}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedAmount(null);
                  setValue("customCredits", value);
                  setTotalCredits(value);

                  // Clear errors first
                  clearErrors("customCredits");

                  // Trigger validation for multiple of 5
                  const numValue = parseInt(value);
                  if (numValue > 0 && numValue % 5 !== 0) {
                    setError("customCredits", {
                      type: "manual",
                      message: t("amount_must_be_multiple_of_5"),
                    });
                  }
                }}
              />
            </FormItem>
            <span className="text-muted">{t("credits")}</span>
          </div>
          {errors?.customCredits && (
            <Error error={errors.customCredits.message} />
          )}
        </div>

        {+totalCredits > 0 && +totalCredits % 5 === 0 && (
          <div
            className="d-flex justify-content-between align-items-center p-2 mt-3"
            style={{
              border: `1px solid #f5f5f5`,
              borderRadius: "5px",
              background: "#f5f5f5",
            }}
          >
            <span className="fw-bold">{`${t("total")}:`}</span>
            {priceLoading ? (
              <div className="d-flex justify-content-center">
                <CustomLoader />
              </div>
            ) : (
              <span className="fw-bold fs-5">{`${currency} ${getTotalPrice()}`}</span>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <FilterButton type="button" onClick={handleClose}>
          {t("cancel")}
        </FilterButton>
        <AddFormButton
          type="button"
          onClick={handlePurchase}
          disabled={
            +totalCredits === 0 ||
            +totalCredits % 5 !== 0 ||
            //saveTopUpLoading ||
            priceLoading
          }
        >
          {/* {saveTopUpLoading ? <CustomLoader /> : t("purchase_credits")} */}
          {t("purchase_credits")}
        </AddFormButton>
      </Modal.Footer>
    </Modal>
  );
};

export default TopUpCreditsModal;

//styles

const PredefinedOptionCard = styled.div`
  min-width: 100px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${(props) => (props.selected ? "#007bff" : "#dee2e6")};
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  background-color: ${(props) => (props.selected ? "#f8f9fa" : "transparent")};

  &:hover {
    background-color: #f5f5f5;
    /*border-color: #007bff;*/
    /*transform: translateY(-1px);*/
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  p {
    margin: 0;

    &:first-child {
      font-weight: 500;
    }
  }
`;
